import { cacher } from "../../utility/common/rtkQueryCacheUtils"
import { queryApi } from "../api"
import { fastRepliesHotKeysTag } from "../tags"
import {
    DeleteFastReplyHotKeyRequest,
    FastRepliesHotKeyWithId,
    SetFastReplyHotKeyRequest,
    WithId
} from "../../models/fastRepliesHotKeys"
import { handleQueryApiError } from "../utility/error"
import { DELETE_HOT_KEY_FAILED, SET_HOT_KEY_FAILED } from "../../store/dialogs/constants"
import store from "../../store/store"
import { actions } from "../../store/dialogs/slice"

export const fastRepliesHotKeysApi = queryApi.injectEndpoints({
    endpoints(build) {
        return {
            getHotKeysByCatalog: build.query<FastRepliesHotKeyWithId[], string>({
                query: catalogCode => ({
                    url: `fast_replies_hotkeys/catalog/${catalogCode}`
                }),
                providesTags(result, error, arg) {
                    return cacher.cacheByIdArg(fastRepliesHotKeysTag)(result, error, arg)
                }
            }),
            createHotKey: build.mutation<FastRepliesHotKeyWithId, SetFastReplyHotKeyRequest>({
                query: ({ articles, ...body }) => ({
                    url: `fast_replies_hotkeys`,
                    method: "POST",
                    body
                }),
                async onQueryStarted({ articles }, { dispatch, queryFulfilled }) {
                    try {
                        const hotKeyResponse = await queryFulfilled

                        const state = store.getState()
                        const hotKeys = state.dialogs.fastReplies.catalogHotKeys

                        dispatch(
                            actions.setFastRepliesHotKeys({
                                hotKeys: [...hotKeys, hotKeyResponse.data],
                                articles: articles
                            })
                        )
                    } catch (e) {
                        handleQueryApiError(dispatch, SET_HOT_KEY_FAILED, e)
                    }
                }
            }),
            updateHotKey: build.mutation<FastRepliesHotKeyWithId, SetFastReplyHotKeyRequest & WithId>({
                query: ({ Id, articles, ...body }) => ({
                    url: `fast_replies_hotkeys/${Id}`,
                    method: "PUT",
                    body
                }),
                async onQueryStarted({ articles }, { dispatch, queryFulfilled }) {
                    try {
                        const hotKeyResponse = await queryFulfilled

                        const state = store.getState()
                        const hotKeys = state.dialogs.fastReplies.catalogHotKeys

                        const changedHotKeyIndex = hotKeys.findIndex(hotKey => hotKey.Id === hotKeyResponse.data.Id)

                        if (changedHotKeyIndex === -1) {
                            return
                        }

                        const updatedHotKey = {
                            ...hotKeys[changedHotKeyIndex],
                            FirstKey: hotKeyResponse.data.FirstKey,
                            SecondKey: hotKeyResponse.data.SecondKey,
                            CombinationName: hotKeyResponse.data.CombinationName
                        }

                        dispatch(
                            actions.setFastRepliesHotKeys({
                                hotKeys: [
                                    ...hotKeys.filter(hotKey => hotKey.Id !== hotKeyResponse.data.Id),
                                    updatedHotKey
                                ],
                                articles: articles
                            })
                        )
                    } catch (e) {
                        handleQueryApiError(dispatch, SET_HOT_KEY_FAILED, e)
                    }
                }
            }),
            deleteHotKey: build.mutation<void, DeleteFastReplyHotKeyRequest>({
                query: ({ id }) => ({
                    url: `fast_replies_hotkeys/${id}`,
                    method: "DELETE"
                }),
                async onQueryStarted({ id, articles }, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled

                        const state = store.getState()
                        const hotKeys = state.dialogs.fastReplies.catalogHotKeys

                        dispatch(
                            actions.setFastRepliesHotKeys({
                                hotKeys: hotKeys.filter(hotKey => hotKey.Id !== id),
                                articles: articles
                            })
                        )
                    } catch (e) {
                        handleQueryApiError(dispatch, DELETE_HOT_KEY_FAILED, e)
                    }
                }
            })
        }
    }
})

export const {
    useLazyGetHotKeysByCatalogQuery,
    useCreateHotKeyMutation,
    useUpdateHotKeyMutation,
    useDeleteHotKeyMutation
} = fastRepliesHotKeysApi
